import { useMutation, useQuery } from '@tanstack/react-query';

import { TOASTTYPE, useToast } from '@/ui/Toast/Toast';
import { fetcher } from '@/utils/fetcher';

import { RETAIL_API_URL } from 'src/utils/envConfig';
import { Unit } from '@/types/Unit';
import { useCurrentAuthSession } from '@/context/AuthContext';
import { getWindowLogger } from '@/utils/remoteLogger';
import { fsPath } from 'src/__path';
import { LogAction, LogStatus } from '@/types/Logging';
import { useIntl } from 'react-intl';
import { useUserInfo } from './useUserInfo';

const logger = getWindowLogger({
  file: `${fsPath}/useScripts.ts`,
});

export type Script = {
  readonly script: string;
  readonly contentPayload: {
    readonly triggers: {
      readonly sensor: string;
      readonly value: string;
      readonly slideIds: readonly string[];
      readonly initialState: null | string;
    };
  };
};

export const useReadScripts = (unitId: Unit['id']) => {
  const { data, isLoading, refetch, isRefetching, isSuccess, isError } =
    useQuery({
      queryKey: ['unitId', unitId],
      queryFn: () => {
        return fetcher<Script>(
          `${RETAIL_API_URL}/scripts/read?unitId=${unitId}`,
        );
      },
      gcTime: 0,
      staleTime: 0,
      enabled: Boolean(unitId),
    });

  return { data, isLoading, refetch, isSuccess, isError, isRefetching };
};

export const useSaveScripts = (unitId: Unit['id']) => {
  const { user } = useCurrentAuthSession();
  const { data: userInfo } = useUserInfo();
  const toast = useToast();

  const intl = useIntl();
  const {
    mutateAsync: saveScript,
    isPending: isLoading,
    error,
  } = useMutation({
    mutationFn: async (content: string) => {
      const { statusCode, reason } = await fetcher<{
        readonly statusCode: number;
        readonly reason: string;
      }>(`${RETAIL_API_URL}/scripts/${unitId}`, {
        method: 'POST',
        body: JSON.stringify({ body: content }),
      });
      if (statusCode && statusCode > 399) {
        logger.error({
          action: LogAction.UPDATE,
          status: LogStatus.FAILED,
          user: user?.uid ?? 'unkown-user',
          customer: userInfo?.customer ?? 'unkown-customer',
          unit_id: unitId,
          message: `${reason}`,
        });
        toast({
          mode: TOASTTYPE.Danger,
          title: intl.formatMessage({
            defaultMessage: 'Error! Something went wrong saving the script.',
            id: 'eANWiQ',
          }),
        });
        throw new Error(reason);
      } else {
        logger.info({
          action: LogAction.UPDATE,
          status: LogStatus.SUCCEEDED,
          user: user?.uid ?? 'unkown-user',
          customer: userInfo?.customer ?? 'unkown-customer',
          unit_id: unitId,
        });
      }
    },
    onSuccess: () => {
      toast({
        mode: TOASTTYPE.Saved,
        title: intl.formatMessage({
          defaultMessage: 'Success! 🎉',
          id: 'JUR35N',
        }),
        subtitle: intl.formatMessage({
          defaultMessage: 'Your code has been saved.',
          id: 'orcE88',
        }),
      });
    },
  });

  return { saveScript, isLoading, error };
};
